// Auth-guard data layer — GET /api/me (FRONTEND_SPA_DESIGN.md §3b). THE single
// source of truth for "am I signed in / am I admin / what plan", used by:
//   - the route guards (routes/guards.tsx), via the router context, and
//   - the @clerk/nextjs shim (next-compat/clerk-nextjs.tsx), which in DEV
//     derives a signed-in identity from it so that reused apps/web components
//     reading Clerk's browser session agree with the guards under the
//     server-side dev bypass (which Clerk's browser session cannot see).
//
// The endpoint is apps/server/src/routes/me.ts (Hono):
//   200 { userId, isAdmin, plan, email, name, imageUrl }
//   401 { error: 'Unauthorized' }   — no Clerk session AND no dev bypass
//
// Any non-2xx / network error resolves to `null` — a fail-closed default
// (guarded routes redirect to /sign-in).

export interface Me {
  userId: string;
  isAdmin: boolean;
  /** Normalized users.subscription_status (server: normalizeMePlan). */
  plan: 'free' | 'pro' | 'team';
  email: string | null;
  name: string | null;
  imageUrl: string | null;
}

export async function fetchMe(): Promise<Me | null> {
  try {
    const res = await fetch('/api/me', { credentials: 'include' });
    if (!res.ok) return null;
    return (await res.json()) as Me;
  } catch {
    return null;
  }
}

// ONE /api/me request per page load, shared by every consumer (the router
// context created in main.tsx and each component that renders through the Clerk
// shim). Memoized on the promise, not the value, so concurrent first callers
// coalesce instead of racing a second fetch.
let mePromise: Promise<Me | null> | undefined;

export function meOnce(): Promise<Me | null> {
  mePromise ??= fetchMe();
  return mePromise;
}

/** Drop the memo so the next meOnce() re-asks the server (sign-in / sign-out). */
export function invalidateMe(): void {
  mePromise = undefined;
}

// Router context shape. Populated once at boot in main.tsx
// (`createRouter({ context: { me: meOnce() } })`) per the design doc's
// "router context { me: Promise<Me | null> } populated once at app start".
//
// TODO(G1 implementer): the design doc also calls for re-populating `me` on
// Clerk `signIn`/`signOut` events (`invalidateMe()` + `context.me` re-fetched +
// `router.invalidate()`) so guards re-evaluate without a full page reload. Wire
// a `useClerk().addListener` effect near the router's `<RouterProvider>` mount
// — left undone here since it needs a real Clerk instance to test against.
export interface RouterContext {
  me: Promise<Me | null>;
}
